<template>
  <div
    :class="
      classes(
        n(),
        computedDisabled && n('--disabled'),
        computedChecked && n('--checked'),
        theme && n(`--${theme}`),
        isIconOnly && n('--icon-only')
      )
    "
    role="checkbox"
    :aria-checked="computedChecked"
    :aria-disabled="computedDisabled"
    @click="handleClick"
  >
    <!-- 内容区域 -->
    <div :class="n('content')">
      <div :class="n('main')">
        <div
          v-if="!isIconOnly"
          :class="n('info')"
        >
          <!-- 主文案 -->
          <div
            v-if="label"
            :class="n('label')"
          >
            {{ label }}
          </div>
          <!-- 描述文案 -->
          <div
            v-if="desc"
            :class="n('desc')"
          >
            {{ desc }}
          </div>
        </div>
        <div :class="n('icon-wrapper')">
          <DoneCircleIcon
            v-if="computedChecked"
            :class="n('checked-icon')"
            aria-hidden="true"
          />
          <CheckboxUnselectedOIcon
            v-else
            :class="n('unchecked-icon')"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, inject } from 'virtual-vue';
import { checkboxProps as props } from './checkbox-props';
import { emits, useCheckboxEmits } from './emits';
import { createNamespace } from '../utils/components';
import { CHECKBOX_GROUP_KEY } from './constants';
import { DoneCircleIcon, CheckboxUnselectedOIcon } from '@tencent/wphk-design-icons';
import { useVModel } from '../hooks/use-v-model';

const { name, n, classes } = createNamespace('checkbox');

export default defineComponent({
  name,
  components: {
    DoneCircleIcon,
    CheckboxUnselectedOIcon,
  },
  props,
  emits,
  setup(props) {
    // 注入 CheckboxGroup 上下文
    const checkboxGroup = inject(CHECKBOX_GROUP_KEY, null);

    // 使用 useVModel 管理选中状态（仅在独立使用时）
    const innerChecked = useVModel(props);
    const emit = useCheckboxEmits();

    // 计算属性：是否选中
    const computedChecked = computed(() => {
      if (checkboxGroup) {
        return checkboxGroup.value.value?.includes(props.checkboxValue);
      }
      return innerChecked.value;
    });

    // 计算属性：是否禁用
    const computedDisabled = computed(() => {
      if (checkboxGroup) {
        return checkboxGroup.disabled.value || props.disabled;
      }
      return props.disabled;
    });

    // 计算属性：是否仅显示图标（当没有label和desc时自动判断）
    const isIconOnly = computed(() => !props.label && !props.desc);

    // 点击处理
    const handleClick = (e: Event) => {
      if (computedDisabled.value) return;

      if (checkboxGroup) {
        // 在 CheckboxGroup 中
        const currentValue = checkboxGroup.value.value || [];
        let newValue: Array<string | number | boolean>;

        if (computedChecked.value) {
          // 当前已选中，取消选中
          newValue = currentValue.filter((v: any) => v !== props.checkboxValue);
          checkboxGroup.onChange(newValue);
        } else {
          // 当前未选中，添加到选中列表
          newValue = [...currentValue, props.checkboxValue];
          checkboxGroup.onChange(newValue);
        }
      } else {
        // 独立使用
        const newChecked = !innerChecked.value;
        innerChecked.value = newChecked;  // 触发 v-model 更新
        emit('change', newChecked, { e });
      }
    };

    return {
      n,
      classes,
      computedChecked,
      computedDisabled,
      isIconOnly,
      handleClick,
    };
  },
});
</script>

